import AutoScroll from 'embla-carousel-auto-scroll';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import * as React from 'react';

import { IconLeftArrow } from '@/assets/icons';
import { Button } from '@/components/core/button';
import { useAnalytics } from '@/lib/analytics';
import { ContentType } from '@/lib/constants';
import { cn } from '@/lib/utils';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  isAutoScroll?: boolean;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

type PreviousNextProps = {
  isTestimonial?: boolean;
};
const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const Carousel = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement> & CarouselProps>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      isAutoScroll = false,
      ...props
    },
    ref
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      isAutoScroll
        ? [
            AutoScroll({
              playOnInit: true,
              stopOnMouseEnter: true,
              speed: 1,
              stopOnInteraction: false,
            }),
          ]
        : plugins
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <section
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          aria-label="Carousel"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </section>
      </CarouselContext.Provider>
    );
  }
);
Carousel.displayName = 'Carousel';

type CarouselContentProps = React.HTMLAttributes<HTMLDivElement> & {
  carouselContainerClassName?: string;
};
const CarouselContent = React.forwardRef<HTMLDivElement, CarouselContentProps>(
  ({ carouselContainerClassName, className, ...props }, ref) => {
    const { carouselRef } = useCarousel();

    return (
      <div
        ref={carouselRef}
        className={cn('overflow-hidden 2xl:overflow-visible', carouselContainerClassName)}
      >
        <div
          ref={ref}
          className={cn(
            'flex',
            // orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
            className
          )}
          {...props}
        />
      </div>
    );
  }
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        ref={ref}
        className={cn(
          'min-w-0 shrink-0 grow-0 basis-full',
          orientation === 'horizontal' ? 'pl-0' : 'pt-4',
          className
        )}
        {...props}
      />
    );
  }
);
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & PreviousNextProps
>(({ className, variant = 'default', size = 'icon', isTestimonial, ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev, canScrollNext, api } = useCarousel();
  const { trackCarouselNavigated } = useAnalytics();

  const handlePrev = React.useCallback(() => {
    if (isTestimonial && api) {
      const currentIndex = api.selectedScrollSnap();
      const currentSlideEl = api.slideNodes()[currentIndex];
      const currentCard = currentSlideEl?.dataset.analyticsId;
      trackCarouselNavigated({
        component: ContentType.TESTIMONIAL_SECTION,
        name: currentCard as any,
        context: 'carousel/testimonial section',
      });
    }
    scrollPrev();
  }, [isTestimonial, scrollPrev, trackCarouselNavigated, api]);

  return (
    <>
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'absolute size-10 rounded-full [&_svg]:size-10',
          { hidden: !(canScrollPrev || canScrollNext) },
          orientation === 'horizontal'
            ? '-left-12 top-1/2 -translate-y-1/2'
            : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          className
        )}
        disabled={!canScrollPrev}
        onClick={handlePrev}
        {...props}
      >
        <IconLeftArrow />
        <span className="sr-only">Previous slide</span>
      </Button>
    </>
  );
});
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & PreviousNextProps
>(({ className, variant = 'default', size = 'icon', isTestimonial, ...props }, ref) => {
  const { orientation, scrollNext, canScrollPrev, canScrollNext, api } = useCarousel();
  const { trackCarouselNavigated } = useAnalytics();

  const handleNext = React.useCallback(() => {
    if (isTestimonial && api) {
      const currentIndex = api.selectedScrollSnap();
      const currentSlideEl = api.slideNodes()[currentIndex];
      const currentCard = currentSlideEl?.dataset.analyticsId;
      trackCarouselNavigated({
        component: ContentType.TESTIMONIAL_SECTION,
        name: currentCard as any,
        context: 'carousel/testimonials section',
      });
    }
    scrollNext();
  }, [isTestimonial, scrollNext, trackCarouselNavigated, api]);

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        'absolute size-10 rounded-full [&_svg]:size-10',
        { hidden: !(canScrollPrev || canScrollNext) },
        orientation === 'horizontal'
          ? '-right-12 top-1/2 -translate-y-1/2'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollNext}
      onClick={handleNext}
      {...props}
    >
      <IconLeftArrow className="size-10 rotate-180" />
      <span className="sr-only">Next slide</span>
    </Button>
  );
});
CarouselNext.displayName = 'CarouselNext';

const CarouselDots = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { dotClassName?: string; selectedDotClassName?: string }
>(
  (
    {
      className,
      dotClassName = 'bg-color-background-icons-pagination-white-inactive aspect-[1/1]',
      selectedDotClassName = 'bg-white aspect-[21/8]',
      ...props
    },
    ref
  ) => {
    const { api } = useCarousel();
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

    React.useEffect(() => {
      if (!api) return;

      setScrollSnaps(api.scrollSnapList());

      const onSelect = () => {
        setSelectedIndex(api.selectedScrollSnap());
      };

      api.on('select', onSelect);
      // Initial selection
      onSelect();

      return () => {
        api.off('select', onSelect);
      };
    }, [api]);

    React.useEffect(() => {
      if (api && scrollSnaps.length !== api?.scrollSnapList().length) {
        setScrollSnaps(api.scrollSnapList());
      }
    }, [api, scrollSnaps]);

    const onDotClick = React.useCallback(
      (index: number) => {
        if (!api) return;
        api.scrollTo(index);
      },
      [api]
    );

    if (!scrollSnaps.length) return null;

    return (
      <div ref={ref} className={cn('flex justify-center gap-2 py-4', className)} {...props}>
        {scrollSnaps.map((_, index) => (
          <button
            type="button"
            key={index}
            className={cn(
              'h-3 transform rounded-full transition-all',
              dotClassName,
              selectedIndex === index && selectedDotClassName
            )}
            onClick={() => onDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    );
  }
);
CarouselDots.displayName = 'CarouselDots';

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
};
